import { useEffect, useRef, useState } from 'react';
import type { AvatarFit } from '@underline/shared';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

const MIN_ZOOM = 0.2;
const MAX_ZOOM = 5;
const DISPLAY_SIZE = 320;
const CROP_SIZE = 512;

interface AvatarCropEditorProps {
  open: boolean;
  imageUrl: string | null;
  imgW: number | null;
  imgH: number | null;
  initialFit?: AvatarFit | null;
  onClose: () => void;
  onConfirm: (fit: AvatarFit) => void;
  isSaving?: boolean;
}

export function AvatarCropEditor({
  open,
  imageUrl,
  imgW,
  imgH,
  initialFit,
  onClose,
  onConfirm,
  isSaving,
}: AvatarCropEditorProps) {
  const [zoom, setZoom] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ startX: number; startY: number; posX: number; posY: number } | null>(null);

  useEffect(() => {
    if (!open || !imgW || !imgH) return;
    if (initialFit) {
      const coverCropScale = Math.max(CROP_SIZE / imgW, CROP_SIZE / imgH);
      setZoom(initialFit.scale / coverCropScale);
      setRotate(initialFit.rotate ?? 0);
      setPos({ x: initialFit.x * (DISPLAY_SIZE / CROP_SIZE), y: initialFit.y * (DISPLAY_SIZE / CROP_SIZE) });
    } else {
      setZoom(1);
      setRotate(0);
      setPos({ x: 0, y: 0 });
    }
  }, [open, imgW, imgH, initialFit]);

  if (!imgW || !imgH || !imageUrl) return null;

  const coverScale = Math.max(DISPLAY_SIZE / imgW, DISPLAY_SIZE / imgH);
  const displayW = Math.round(imgW * coverScale * zoom);
  const displayH = Math.round(imgH * coverScale * zoom);

  function handlePointerDown(e: React.PointerEvent) {
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, posX: pos.x, posY: pos.y };
  }

  function handlePointerMove(e: React.PointerEvent) {
    e.stopPropagation();
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setPos({ x: dragRef.current.posX + dx, y: dragRef.current.posY + dy });
  }

  function handlePointerUp(e: React.PointerEvent) {
    e.stopPropagation();
    dragRef.current = null;
  }

  function handleConfirm() {
    if (!imgW || !imgH) return;
    const coverCropScale = Math.max(CROP_SIZE / imgW, CROP_SIZE / imgH);
    const fitScale = coverCropScale * zoom;
    const fitX = pos.x * (CROP_SIZE / DISPLAY_SIZE);
    const fitY = pos.y * (CROP_SIZE / DISPLAY_SIZE);
    onConfirm({ img_w: imgW, img_h: imgH, scale: fitScale, rotate, x: fitX, y: fitY });
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="z-[70] fixed inset-0 top-0 left-0 h-dvh w-screen max-w-none translate-x-0 translate-y-0 rounded-none sm:rounded-none border-0 p-0 flex flex-col bg-background">
        <DialogTitle className="sr-only">Ajustar foto de perfil</DialogTitle>

        <div className="flex items-center justify-between px-5 pt-[calc(env(safe-area-inset-top)+16px)] pb-3">
          <h2 className="u-h3">Ajustar foto de perfil</h2>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 overflow-hidden">
          <div
            className="relative overflow-hidden rounded-2xl bg-surface-2 touch-none select-none"
            style={{ width: DISPLAY_SIZE, height: DISPLAY_SIZE }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          >
            <img
              src={imageUrl}
              alt=""
              draggable={false}
              className="absolute max-w-none"
              style={{
                width: displayW,
                height: displayH,
                left: '50%',
                top: '50%',
                transform: `translate(-50%, -50%) translate(${pos.x}px, ${pos.y}px) rotate(${rotate}deg)`,
              }}
            />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
                clipPath: `polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 ${DISPLAY_SIZE * 0.15}px, ${DISPLAY_SIZE * 0.15}px ${DISPLAY_SIZE * 0.15}px, ${DISPLAY_SIZE * 0.85}px ${DISPLAY_SIZE * 0.15}px, ${DISPLAY_SIZE * 0.85}px ${DISPLAY_SIZE * 0.85}px, ${DISPLAY_SIZE * 0.15}px ${DISPLAY_SIZE * 0.85}px, ${DISPLAY_SIZE * 0.15}px ${DISPLAY_SIZE}px, 0 ${DISPLAY_SIZE}px)`,
              }}
            />
            <div
              className="absolute pointer-events-none border-2 border-white/80 rounded-lg"
              style={{
                left: DISPLAY_SIZE * 0.15 - 1,
                top: DISPLAY_SIZE * 0.15 - 1,
                width: DISPLAY_SIZE * 0.7 + 2,
                height: DISPLAY_SIZE * 0.7 + 2,
              }}
            />
          </div>
        </div>

        <div className="px-6 py-4 space-y-3">
          <div className="flex items-center gap-3">
            <span className="mono text-xs uppercase text-muted-foreground w-14 shrink-0">Zoom</span>
            <input
              type="range"
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1"
            />
          </div>
          <div className="flex items-center gap-3">
            <span className="mono text-xs uppercase text-muted-foreground w-14 shrink-0">Girar</span>
            <input
              type="range"
              min={-180}
              max={180}
              step={1}
              value={rotate}
              onChange={(e) => setRotate(Number(e.target.value))}
              className="flex-1"
            />
          </div>

          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border border-border bg-card px-4 py-3 text-sm font-medium hover:bg-muted"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isSaving}
              className="flex-[2] rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-red disabled:opacity-60"
            >
              {isSaving ? 'Salvando...' : 'Usar foto'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
